import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SSO_PORTAL_CLIENT_ID } from '../auth/oidcConfig';
import { markSynced } from '../auth/sessionSyncMarker';
import { navigateAway } from '../lib/tabCloseSuppression';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

// Matches backend/src/lib/authSession.js's SESSION_SYNC_STATE_COOKIE.
const SYNC_STATE_COOKIE = 'bescom_sso_sync_state';

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function clearSyncStateCookie() {
  document.cookie = `${SYNC_STATE_COOKIE}=; Max-Age=0; path=/`;
}

// Decodes just the `sid` claim out of an id_token's payload — no signature
// verification needed: this came directly from OUR OWN https fetch to
// Authentik's token endpoint moments ago (below), not anything user-
// supplied. Worst case a bad/missing decode just means the sid-based
// revocation check in backchannelLogout.js falls back to its pre-existing
// blanket behavior for this user — not a security issue either way,
// bescom_token remains independently verified everywhere else. See
// lib/currentSid.js on the backend for what this feeds into.
function decodeSidFromIdToken(idToken) {
  if (!idToken) return null;
  try {
    const payload = idToken.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json).sid || null;
  } catch (e) {
    return null;
  }
}

// This route only exists to catch the redirect from Authentik's OIDC
// authorize endpoint during session-sync (see LoginPage.jsx / login.js
// backend). Landing here at all confirms Authentik's redirect_uri
// allowlist accepted us, which means the browser now holds a genuine
// Authentik session cookie.
//
// CONFIRMED BUG (2026-08-13): the returned `code` used to be discarded
// entirely — our real app token was already issued earlier by the custom
// login flow, so it looked unnecessary. Turns out that also meant this
// app's own OAuth2 client (SSO_PORTAL_CLIENT_ID) never actually completed
// an authorization grant with Authentik — and Back-Channel Logout
// dispatch depends on Authentik knowing a given client issued a token for
// a given session. Without that exchange, Authentik had no reason to
// ever call this app's own backchannel-logout receiver when a logout
// happened elsewhere — confirmed via backend logs showing zero
// invocations despite a real logout from another app. Exchanging the
// code now (result discarded — bescom_token remains this app's real
// credential, nothing here touches oidc-client-ts's own user state)
// fixes that without changing anything else about this app's login.
export default function SsoSyncCallback() {
  const navigate = useNavigate();
  // StrictMode double-invokes effects in dev. bescom_needs_profile is a
  // read-once flag (we delete it as we read it), so a naive effect here
  // reads '1' -> navigates to /complete-profile on the first pass, then
  // reads null (already deleted) -> navigates to /dashboard on the second
  // pass, silently clobbering the first navigation. Guard so this only
  // ever runs once regardless of how many times the effect fires.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const finish = async () => {
      // Landing here at all means a real sync round trip through Authentik
      // just succeeded (see file header) — record it so Login.jsx/
      // Dashboard.jsx can skip a redundant one for a while. See
      // sessionSyncMarker.js for why this matters (grant-supersession /
      // spurious Back-Channel Logout bug).
      markSynced();
      // CONFIRMED BUG (2026-09-08): originally also called
      // clearGatewayAttempts() here on the theory that landing on this page
      // means the loop is resolved — wrong. Authentik's own session-end bug
      // can kill the outpost's session again moments after this "succeeds",
      // bouncing back to Login.jsx's gateway again. Clearing the counter on
      // every round trip that merely COMPLETES (not one that actually
      // stuck) let the loop cycle in an unbounded fast-path-fails ->
      // forced-sync-"succeeds"-and-resets -> fast-path-fails-again pattern,
      // never reaching MAX_NEXT_ATTEMPTS. Leaving the counter alone here and
      // letting it expire only via its own time window (see
      // sessionSyncMarker.js) means a genuinely resolved case still clears
      // naturally (no bounce-back within the window at all), while a
      // persistent one actually escalates to the hard cap as intended.

      // Complete the code exchange so Authentik registers a real grant
      // for this client — see the file header comment above. Best-effort:
      // a failure here just means Back-Channel Logout to this app won't
      // fire for this particular session, not a broken login.
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const returnedState = params.get('state');
      // CSRF-binds this callback to the specific sessionSyncUrl request
      // that started it — see authSession.js's own comment on
      // createSessionSyncLink/state. An attacker-crafted callback URL
      // (their own `code`, no matching cookie) fails this check and the
      // exchange is skipped, same non-fatal path as any other failure here.
      const expectedState = readCookie(SYNC_STATE_COOKIE);
      clearSyncStateCookie();
      // ADDED (2026-09-04): sid, decoded from the id_token this exchange
      // returns — the response used to be entirely discarded. This is the
      // exact same `sid` Authentik later puts in a logout_token for this
      // session, forwarded to /account/capture-session below so
      // backchannelLogout.js can tell a real revocation of THIS session
      // apart from a stale notification about an already-superseded grant.
      // See lib/currentSid.js on the backend.
      let sid = null;
      if (code && expectedState && returnedState === expectedState) {
        try {
          const authentikOrigin = new URL(process.env.REACT_APP_AUTHENTIK_AUTHORITY).origin;
          const tokenRes = await fetch(`${authentikOrigin}/application/o/token/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'authorization_code',
              code,
              redirect_uri: `${window.location.origin}/sso-sync-callback`,
              client_id: SSO_PORTAL_CLIENT_ID,
            }),
          });
          const tokenData = await tokenRes.json();
          sid = decodeSidFromIdToken(tokenData.id_token);
        } catch (e) {
          // Non-fatal — see comment above.
        }
      }

      // Best-effort: captures a session for our OWN backend to persist
      // (closes the add-phone/add-email gap for accounts that never got
      // one via a normal login). Deliberately done HERE, not alongside
      // whatever recovery token got the browser to this page — confirmed
      // that generating a second recovery token for the same user while
      // an earlier one is still outstanding invalidates it ("Request has
      // been denied" on Authentik's side). By now that earlier token has
      // already been fully consumed (its real navigation is what got us
      // here), so this one is never outstanding at the same time.
      // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
      // header comment) — no more synchronous "is there a token" check;
      // credentials: 'include' sends the cookie automatically if the
      // earlier login/registration completion set one, same as before.
      // RE-ENABLED (2026-09-03): disabling this to test a repeated-logout
      // theory did NOT stop the logouts (confirmed live) — ruled out as
      // the cause FOR THE broader cross-app case tested at the time. See
      // backchannelLogout.js for that finding.
      //
      // REVERTED (2026-09-05): briefly delayed this call to test a
      // "two login-stage completions too close together" theory for a
      // separate, rare spurious-logout case. Ruled out by live evidence:
      // confirmed multiple DIFFERENT, unrelated users' sessions (across
      // different apps) getting Back-Channel-Logged-out within
      // milliseconds of each other, repeating on a roughly 3.5-4 minute
      // cadence — a pattern only explainable by something periodic and
      // system-level on Authentik's own side (a pod restart, cache/Redis
      // flush, or scheduled task), not by anything in this app's own
      // per-request timing. No reason to keep the extra wait.
      try {
        await fetch(`${API_BASE}/account/capture-session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sid }),
        });
      } catch (e) {
        // Non-fatal — add-phone/add-email would just need one manual
        // login, same as before this existed.
      }

      // Set by Login.jsx when arriving via ?next= (e.g. an outpost-fronted
      // app's Redirect Stage) — Authentik's session cookie is genuinely
      // set now (we just landed here via a real navigation through it),
      // so it's safe to send the browser on to the original app. Normal
      // logins (no ?next=) never set this, so they fall through to the
      // existing behavior below unchanged.
      const pendingNext = localStorage.getItem('bescom_pending_next');
      if (pendingNext) {
        localStorage.removeItem('bescom_pending_next');
        navigateAway(pendingNext);
        return;
      }

      const needsProfile = localStorage.getItem('bescom_needs_profile');
      localStorage.removeItem('bescom_needs_profile');
      navigate(needsProfile ? '/complete-profile' : '/dashboard', { replace: true });
    };

    finish();
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh', boxSizing: 'border-box',
      backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)', backgroundSize: 'cover', backgroundPosition: 'center',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.65)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.5)', borderRadius: '16px',
        padding: '32px 40px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
      }}>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Finishing sign-in…</p>
      </div>
    </div>
  );
}
